import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { get, post } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { dateOnly } from '@/lib/utils';
import { printDocument } from '@/lib/print';
import { Dialog } from '@/components/ui/layout';
import { Badge, Button, Card, CardContent, CardHeader, CardTitle, Field, Input } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';

export type CertificateType = 'sick_leave' | 'fitness' | 'medical' | 'attendance';

interface CertificateRow {
  id: string;
  type: string;
  number: string | null;
  fromDate: string | null;
  toDate: string | null;
  days: number | null;
  issuedAt: string;
  voidedAt: string | null;
}

export const CERTIFICATE_TYPE_LABELS: Record<CertificateType, string> = {
  sick_leave: 'Sick leave',
  fitness: 'Fitness',
  medical: 'Medical certificate',
  attendance: 'Attendance',
};

export function certificateBody(type: CertificateType, patientName: string, from: string, to: string, remarks: string): string {
  const period = from && to ? ` from ${from} to ${to}` : '';
  const text: Record<CertificateType, string> = {
    sick_leave: `This is to certify that ${patientName} was examined and is advised rest${period}.`,
    fitness: `This is to certify that ${patientName} was examined and is fit to resume normal duties${from ? ` from ${from}` : ''}.`,
    medical: `This is to certify that ${patientName} was examined at this hospital.`,
    attendance: `This is to certify that ${patientName} attended this hospital for consultation today.`,
  };
  return [text[type], remarks.trim()].filter(Boolean).join('\n');
}

export async function printCertificate(id: string) {
  const doc = await get<{ html: string }>(`/certificates/${id}/print`);
  printDocument(doc.html);
}

export function CertificatesPanel({ encounterId }: { encounterId: string }) {
  const { can } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [voiding, setVoiding] = useState<CertificateRow | null>(null);
  const [reason, setReason] = useState('');

  const certificates = useQuery({
    queryKey: ['encounter-certificates', encounterId],
    queryFn: () => get<CertificateRow[]>(`/encounters/${encounterId}/certificates`),
  });

  const cancel = useMutation({
    mutationFn: () => post(`/certificates/${voiding?.id}/void`, { reason: reason.trim() }),
    onSuccess: () => {
      toast.success(tr('opd', 'certificate_withdrawn'));
      setVoiding(null);
      setReason('');
      void queryClient.invalidateQueries({ queryKey: ['encounter-certificates', encounterId] });
    },
    onError: (error) => toast.error(tr('opd', 'could_not_withdraw_the_certificate'), describeError(error)),
  });

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between">
        <CardTitle className="text-sm">{tr('opd', 'certificates')}</CardTitle>
        {can('certificate.issue') && (
          <Button size="sm" variant="outline" onClick={() => navigate(`/consultations/${encounterId}/certificates/new`)}>
            {tr('opd', 'issue_certificate')}
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-2">
        {(certificates.data ?? []).length === 0 && <p className="text-sm text-muted-foreground">{tr('opd', 'none_issued_in_this_visit')}</p>}
        {certificates.data?.map((certificate) => (
          <div key={certificate.id} className="flex items-center justify-between rounded-md border p-2 text-sm">
            <div>
              <p>
                {CERTIFICATE_TYPE_LABELS[certificate.type as CertificateType] ?? certificate.type}
                {certificate.number ? ` · ${certificate.number}` : ''}
                {certificate.days ? ` · ${certificate.days} day${certificate.days === 1 ? '' : 's'}` : ''}
              </p>
              <p className="text-xs text-muted-foreground">{dateOnly(certificate.issuedAt)}</p>
            </div>
            <div className="flex items-center gap-2">
              {certificate.voidedAt && <Badge variant="destructive">{tr('opd', 'withdrawn')}</Badge>}
              <Button size="sm" variant="ghost" onClick={() => void printCertificate(certificate.id)}>
                {tr('opd', 'print')}
              </Button>
              {!certificate.voidedAt && can('certificate.issue', 'encounter.cover') && (
                <Button size="sm" variant="ghost" onClick={() => setVoiding(certificate)}>
                  {tr('opd', 'withdraw')}
                </Button>
              )}
            </div>
          </div>
        ))}
      </CardContent>

      {voiding && (
        <Dialog
          open
          onClose={() => setVoiding(null)}
          title={tr('opd', 'withdraw_the_certificate')}
          description={tr('opd', 'the_print_will_read_cancelled_with_the_reason')}
          footer={
            <>
              <Button variant="ghost" onClick={() => setVoiding(null)}>
                {tr('opd', 'keep_it')}
              </Button>
              <Button variant="destructive" onClick={() => cancel.mutate()} loading={cancel.isPending} disabled={reason.trim().length < 5}>
                {tr('opd', 'withdraw')}
              </Button>
            </>
          }
        >
          <Field label={tr('opd', 'reason')} required>
            <Input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={400} />
          </Field>
        </Dialog>
      )}
    </Card>
  );
}
